import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, Clock, ChevronDown, ChevronRight, History } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Run {
  id: string;
  batch_type: string;
  created_at: string;
  status: string;
  entry_count: number;
  company: string;
}

interface HistoryDropdownProps {
  batchType: 'bank_entry' | 'purchase' | 'sales';
  collapsed?: boolean;
}

export function HistoryDropdown({ batchType, collapsed = false }: HistoryDropdownProps) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!expanded) return;

    let isMounted = true;
    setLoading(true);

    const fetchRuns = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        // Server-side filtering by batch_type for accurate results
        const response = await fetch(`${API_URL}/api/historical-runs/recent?limit=5&batch_type=${batchType}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch');

        const data: Run[] = await response.json();
        if (isMounted) {
          setRuns(data);
        }
      } catch (err) {
        console.error('Failed to load runs:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRuns();

    return () => { isMounted = false; };
  }, [expanded, batchType]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'failed': return <XCircle className="h-3 w-3 text-red-500" />;
      default: return <Clock className="h-3 w-3 text-yellow-500" />;
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffMs / 86400000)}d ago`;
  };

  if (collapsed) return null;

  return (
    <div className="ml-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center gap-2 px-2 py-1.5 w-full rounded text-xs",
          "text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        )}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <History className="h-3 w-3" />
        <span>History</span>
      </button>

      {expanded && (
        <div className="ml-4 mt-1 space-y-1">
          {loading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            </div>
          ) : runs.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-1">No runs yet</p>
          ) : (
            runs.map((run) => (
              <div
                key={run.id}
                onClick={() => navigate(`/preview/${run.id}`)}
                className={cn(
                  "flex items-center justify-between px-2 py-1.5 rounded cursor-pointer",
                  "hover:bg-accent transition-colors"
                )}
              >
                <div className="flex items-center gap-1.5">
                  {getStatusIcon(run.status)}
                  <span className="text-xs truncate max-w-[100px]">{run.company}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">{run.entry_count}</Badge>
                  <span className="text-[10px] text-muted-foreground">{formatTime(run.created_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default HistoryDropdown;
